import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, CheckCircle2, Briefcase, Home as HomeIcon, Users, X, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { api, friendlyError } from '../lib/api';

const PER_PAGE = 24;

export default function AgencyDirectory() {
  const [sp, setSp] = useSearchParams();
  const [agencies, setAgencies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState(sp.get('q') || '');
  const [city, setCity] = useState(sp.get('city') || '');
  const [specialty, setSpecialty] = useState(sp.get('specialty') || '');
  const [verified, setVerified] = useState(sp.get('verified') === 'true');
  const [minProperties, setMinProperties] = useState(sp.get('min_properties') || '');
  const [minMembers, setMinMembers] = useState(sp.get('min_members') || '');

  const fetchAgencies = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const data = await api.listAgencies({
        q: sp.get('q'),
        city: sp.get('city'),
        specialty: sp.get('specialty'),
        verified: sp.get('verified') === 'true' ? true : undefined,
        min_properties: sp.get('min_properties'),
        min_members: sp.get('min_members'),
        limit: PER_PAGE,
      });
      setAgencies(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(friendlyError(e, 'No pudimos cargar las inmobiliarias.'));
    } finally {
      setLoading(false);
    }
  }, [sp]);

  useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

  const apply = () => {
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (city.trim()) next.set('city', city.trim());
    if (specialty.trim()) next.set('specialty', specialty.trim());
    if (verified) next.set('verified', 'true');
    if (minProperties) next.set('min_properties', minProperties);
    if (minMembers) next.set('min_members', minMembers);
    setSp(next);
  };

  const clearAll = () => {
    setQ(''); setCity(''); setSpecialty(''); setVerified(false);
    setMinProperties(''); setMinMembers('');
    setSp(new URLSearchParams());
  };

  const hasFilters = !!(
    sp.get('q') || sp.get('city') || sp.get('specialty') ||
    sp.get('verified') || sp.get('min_properties') || sp.get('min_members')
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Directorio de inmobiliarias en Colombia',
    description: 'Inmobiliarias verificadas con propiedades activas y equipos de asesores en habita.co.',
    numberOfItems: total,
  };

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title="Inmobiliarias verificadas en Colombia"
        description="Encuentra la inmobiliaria adecuada en Colombia. Filtra por ciudad, especialidad y tamaño del equipo. Inmobiliarias con propiedades activas y asesores verificados en habita.co."
        jsonLd={jsonLd}
      />
      <Header />

      <section className="bg-habita-dark text-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h1 className="font-serif text-3xl sm:text-5xl font-bold">Inmobiliarias en Colombia</h1>
          <p className="text-white/80 mt-3 max-w-2xl">
            Empresas verificadas del sector, con equipos de asesores y portafolios activos. Encuentra la inmobiliaria adecuada para tu próxima compra, arriendo o publicación.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); apply(); }}
            className="mt-6 bg-white text-habita-dark rounded-2xl p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
            data-testid="agency-directory-filters"
          >
            <div className="md:col-span-4 flex items-center gap-2 px-3 min-w-0">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Nombre de la inmobiliaria…"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agency-directory-q"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="Ciudad"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agency-directory-city"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={specialty}
                onChange={e => setSpecialty(e.target.value)}
                placeholder="Especialidad"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agency-directory-specialty"
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2 px-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" data-testid="agency-directory-verified-label">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={e => setVerified(e.target.checked)}
                  className="rounded border-gray-300 text-habita focus:ring-habita"
                  data-testid="agency-directory-verified"
                />
                Verificadas
              </label>
              <Button type="submit" className="btn-habita rounded-full px-5 h-10" data-testid="agency-directory-apply">Buscar</Button>
            </div>

            {/* Min properties / Min members — second row */}
            <div className="md:col-span-6 flex items-center gap-2 px-3 min-w-0">
              <HomeIcon className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                value={minProperties}
                onChange={e => setMinProperties(e.target.value)}
                placeholder="Mín. de propiedades activas"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agency-directory-min-properties"
              />
            </div>
            <div className="md:col-span-6 flex items-center gap-2 px-3 md:border-l border-gray-200 min-w-0">
              <Users className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                value={minMembers}
                onChange={e => setMinMembers(e.target.value)}
                placeholder="Mín. de asesores"
                className="flex-1 min-w-0 border-0 focus-visible:ring-0 h-10 px-0 text-habita-dark placeholder:text-gray-400 bg-transparent"
                data-testid="agency-directory-min-members"
              />
            </div>
          </form>

          {hasFilters && (
            <button
              onClick={clearAll}
              className="text-xs text-white/70 hover:text-white mt-3 inline-flex items-center gap-1"
              data-testid="agency-directory-clear"
            >
              <X className="w-3 h-3" /> Limpiar filtros
            </button>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <p className="text-sm text-gray-600 mb-5" data-testid="agency-directory-count">
          {loading ? 'Cargando…' : `${total} ${total === 1 ? 'inmobiliaria' : 'inmobiliarias'}`}
        </p>

        {err && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm" data-testid="agency-directory-error">
            {err}
          </div>
        )}

        {!loading && agencies.length === 0 && !err && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center" data-testid="agency-directory-empty">
            <p className="font-serif text-xl font-bold text-habita-dark">Aún no hay inmobiliarias que coincidan</p>
            <p className="text-sm text-gray-600 mt-2">Prueba ajustando los filtros o explora nuestras propiedades destacadas.</p>
            <Link to="/buscar" className="inline-block mt-4 text-sm font-semibold text-habita hover:underline">Ver propiedades →</Link>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {agencies.map(a => <AgencyCard key={a.id} a={a} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function AgencyCard({ a }) {
  const [coverErr, setCoverErr] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const showCover = !!a.cover_url && !coverErr;
  const showLogo = !!a.logo_url && !logoErr;
  return (
    <Link
      to={`/inmobiliaria/${a.slug}`}
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-habita transition-all group"
      data-testid={`agency-card-${a.id}`}
    >
      <div className="h-28 bg-habita-mint relative">
        {showCover
          ? <img src={a.cover_url} alt="" className="w-full h-full object-cover" onError={() => setCoverErr(true)} />
          : <div className="w-full h-full bg-gradient-to-br from-habita to-emerald-600" />}
        {a.verified && (
          <span
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-emerald-700 text-[10px] font-bold shadow-sm"
            data-testid={`agency-card-verified-${a.id}`}
          >
            <CheckCircle2 className="w-3 h-3" /> VERIFICADA
          </span>
        )}
      </div>
      <div className="px-5 pb-5 -mt-9 relative">
        <div className="w-16 h-16 rounded-2xl bg-white border-4 border-white shadow-md overflow-hidden">
          {showLogo
            ? <img src={a.logo_url} alt={a.name} className="w-full h-full object-cover" onError={() => setLogoErr(true)} />
            : <div className="w-full h-full bg-habita-mint flex items-center justify-center text-habita">
                <Building2 className="w-7 h-7" />
              </div>}
        </div>

        <div className="mt-3">
          <p className="font-serif font-bold text-habita-dark text-lg group-hover:text-habita transition-colors truncate">
            {a.name}
          </p>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {a.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {a.city}</span>}
            {a.founded_year && <span>· Desde {a.founded_year}</span>}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-habita-cream rounded-xl px-2.5 py-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Propiedades</p>
            <p className="font-serif text-base font-bold text-habita-dark tabular-nums flex items-center gap-1">
              <HomeIcon className="w-3.5 h-3.5 text-habita" /> {a.active_properties ?? 0}
            </p>
          </div>
          <div className="bg-habita-cream rounded-xl px-2.5 py-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Asesores</p>
            <p className="font-serif text-base font-bold text-habita-dark tabular-nums flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-habita" /> {a.active_members ?? 0}
            </p>
          </div>
        </div>

        {a.specialties?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {a.specialties.slice(0, 3).map(s => (
              <span key={s} className="px-2 py-0.5 rounded-full bg-habita-mint text-habita text-[10px] font-semibold">
                {s}
              </span>
            ))}
            {a.specialties.length > 3 && (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold">
                +{a.specialties.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-habita opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`agency-card-cta-${a.id}`}>
          Ver perfil →
        </div>
      </div>
    </Link>
  );
}
